import { useState, useRef } from 'react';
import useCmsPage from '../hooks/useCmsPage';
import useSeoMeta from '../hooks/useSeoMeta';
import { submitContact } from '../api/api';
import './Contact.css';

export default function Contact() {
  const { sections, page } = useCmsPage('contact');
  useSeoMeta(page, { title: 'Contact — Tech Talent Accelerator', desc: "Contactez l'équipe TTA pour une candidature, un partenariat ou toute question." });
  const getSec = type => sections.find(s => s.type === type)?.content || {};
  const heroContent = getSec('hero');
  const cmsInfo = getSec('contact_info');

  const [form, setForm] = useState({ nom: '', email: '', sujet: '', message: '', type: 'general' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef(null);
  const goToForm = (type) => {
    setForm(f => ({ ...f, type }));
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handle = e => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async e => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await submitContact(form);
      setSent(true);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue. Réessayez.');
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="contact-page">
      <section className="contact-hero">
        <div className="contact-hero__bg" />
        <div className="page-hero-right__orb" data-parallax="0.55" />
        <div className="page-hero-right__orb2" data-parallax="-0.20" />
        <div className="page-hero-layout">
          <div className="page-hero-left">
            <div className="section-eyebrow">{heroContent.eyebrow || 'Contact'}</div>
            <h1 className="section-title" style={{ fontSize: '42px', maxWidth: '480px' }}>
              {heroContent.title || <>Parlons de votre <span className="grad-text">projet</span></>}
            </h1>
            <p className="section-subtitle" style={{ maxWidth: '420px' }}>
              {heroContent.subtitle || "Candidature, partenariat, financement ou simple question — notre équipe vous répond sous 24h."}
            </p>
            <div className="hero-filter-bar">
              {[
                { key: 'general',     label: 'Question générale' },
                { key: 'bailleur',    label: 'Partenariat' },
                { key: 'recrutement', label: 'Recrutement' },
              ].map(f => (
                <button key={f.key} className={`hero-filter-btn ${form.type === f.key ? 'active' : ''}`} onClick={() => goToForm(f.key)}>{f.label}</button>
              ))}
            </div>
          </div>
          <div className="page-hero-right">
            <div className="phm-grid">
              {[
                { num: '< 24h', label: 'Délai de réponse garanti', bar: '95%', delay: '0s', barDelay: '0.2s',
                  icon: <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></> },
                { num: '5j/7', label: 'Disponibilité de notre équipe', bar: '100%', delay: '1.5s', barDelay: '0.4s',
                  icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
                { num: '4', label: 'Canaux de contact disponibles', bar: '80%', delay: '3s', barDelay: '0.6s',
                  icon: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></> },
                { num: '100%', label: 'Demandes traitées et suivies', bar: '100%', delay: '4.5s', barDelay: '0.8s',
                  icon: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></> },
              ].map((m, i) => (
                <div key={i} className="phm-card" style={{ animationDelay: m.delay }}>
                  <div className="phm-card__icon">
                    <svg viewBox="0 0 24 24">{m.icon}</svg>
                  </div>
                  <div className="phm-card__num">{m.num}</div>
                  <div className="phm-card__label">{m.label}</div>
                  <div className="phm-card__bar">
                    <div className="phm-card__bar-fill" style={{ width: m.bar, animationDelay: m.barDelay }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section" ref={formRef}>
        <div className="contact-layout">
          <div className="contact-form-wrap">
            {sent ? (
              <div className="contact-success">
                <div className="contact-success__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <h3>Message envoyé !</h3>
                <p>Notre équipe vous répondra dans les 24 heures. Merci de votre intérêt pour TTA.</p>
                <button className="btn-primary" style={{ marginTop: '16px' }} onClick={() => setSent(false)}>
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form className="contact-form" onSubmit={submit}>
                <div className="contact-form__types">
                  {[
                    { val: 'general', label: 'Question générale' },
                    { val: 'bailleur', label: 'Partenariat / Financement' },
                    { val: 'recrutement', label: 'Recrutement' },
                  ].map(t => (
                    <button
                      key={t.val}
                      type="button"
                      className={`contact-form__type-btn ${form.type === t.val ? 'active' : ''}`}
                      onClick={() => setForm({ ...form, type: t.val })}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="contact-form__row">
                  <div className="contact-form__field">
                    <label>Nom complet</label>
                    <input name="nom" value={form.nom} onChange={handle} placeholder="Votre nom" required />
                  </div>
                  <div className="contact-form__field">
                    <label>Email</label>
                    <input name="email" type="email" value={form.email} onChange={handle} placeholder="votre@email.com" required />
                  </div>
                </div>
                <div className="contact-form__field">
                  <label>Sujet</label>
                  <input name="sujet" value={form.sujet} onChange={handle} placeholder="Comment pouvons-nous vous aider ?" required />
                </div>
                <div className="contact-form__field">
                  <label>Message</label>
                  <textarea name="message" value={form.message} onChange={handle} placeholder="Décrivez votre projet, votre question ou votre demande..." rows={5} required />
                </div>
                {error && (
                  <p style={{ color: '#e53e3e', fontSize: '13px', marginBottom: '10px' }}>{error}</p>
                )}
                <button type="submit" className="btn-primary" disabled={sending} style={{ width: '100%', justifyContent: 'center', fontSize: '14px', padding: '14px', opacity: sending ? 0.7 : 1 }}>
                  {sending ? 'Envoi en cours…' : 'Envoyer le message →'}
                </button>
              </form>
            )}
          </div>

          <div className="contact-info">
            <div className="contact-info__card">
              <h3 className="contact-info__title">{cmsInfo.card_title || 'Informations de contact'}</h3>
              <div className="contact-info__items">
                <div className="contact-info__item">
                  <div className="contact-info__ico">
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </div>
                  <div>
                    <div className="contact-info__label">Email</div>
                    <a href={`mailto:${cmsInfo.email || 'info@sira-labs.com'}`} className="contact-info__val">{cmsInfo.email || 'info@sira-labs.com'}</a>
                  </div>
                </div>
                <div className="contact-info__item">
                  <div className="contact-info__ico">
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.64 3.24a2 2 0 0 1 1.99-2.17h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.71 2.81a2 2 0 0 1-.45 2.11L7.91 9a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.58 2.81.71A2 2 0 0 1 22 16.92z"/></svg>
                  </div>
                  <div>
                    <div className="contact-info__label">Téléphone</div>
                    <a href={`tel:${(cmsInfo.phone || '+2250575824040').replace(/\s/g,'')}`} className="contact-info__val">{cmsInfo.phone || '+225 0575824040'}</a>
                  </div>
                </div>
                <div className="contact-info__item">
                  <div className="contact-info__ico">
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </div>
                  <div>
                    <div className="contact-info__label">Localisation</div>
                    <div className="contact-info__val">{cmsInfo.location || "Abidjan, Côte d'Ivoire"}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="contact-info__hours">
              <h4>{cmsInfo.hours_title || "Heures d'ouverture"}</h4>
              <p>{cmsInfo.hours_line1 || 'Lundi – Vendredi : 8h00 – 18h00 (GMT)'}</p>
              <p>{cmsInfo.hours_line2 || 'Réponse garantie sous 24h ouvrées'}</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
